import { getFieldErrorMessage } from '@avalara/bat-apps-shared/lib/form-validation';
import type { FormEvent } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { FieldErrorMessage, FormInput } from '../../../components/styled/components/sign-up';
import { isFieldVisitedAndInvalid } from '../../../lib/shared-signup';
import { updateProductAnalytics } from '../../../lib/side-effects/analytics';
import { getRegions } from '../../../lib/side-effects/avatax';
import { selectState } from '../../../selectors';
import { clearFieldForValidation, signupFormChanged } from '../../../store/shared-signup';

import type { InputEvent, NewAccountForm } from '@avalara/types/index';
import type { BaseNewAccountForm } from '../../../../../types';
import { useAppDispatch } from '../../../hooks/use-app-dispatch';

// Also used by FormSelect
export type Props = {
  addPadding?: boolean;
  fieldName: keyof BaseNewAccountForm | keyof NewAccountForm;
  marginAboveError?: string;
  maxLength?: number;
  placeholder?: string;
  type?: string;
  validateDuringInput?: boolean;
  width?: string;
};

const FormInputComponent = ({
  addPadding,
  fieldName,
  marginAboveError,
  maxLength,
  placeholder,
  type,
  validateDuringInput,
  width,
}: Props) => {
  const dispatch = useAppDispatch();
  const {
    sharedSignup,
    offer: { offerFlow },
  } = useSelector(selectState);

  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const showPasswordError = queryParams.get('invalidPassword') === 'true' && fieldName === 'password';

  const fieldMissingOrInvalid = isFieldVisitedAndInvalid(sharedSignup, fieldName) || showPasswordError;
  const value = sharedSignup.signupForm[fieldName] || '';
  const disabled = sharedSignup.disableForm || false;
  const readOnly = !!sharedSignup.readOnlyFields?.includes(fieldName);

  // In some cases (last field), we need to show validation immediately,
  // or else it's confusing to a user why the button is disabled
  const handleFocus = () => {
    if (validateDuringInput) dispatch(clearFieldForValidation(fieldName));

    // If we receive a password validation error from the server, it will be noted via
    // the query param "invalidPassword" so that we can highlight the field on the screen.
    if (showPasswordError) {
      queryParams.delete('invalidPassword');
      history.replace({
        search: queryParams.toString(),
      });
    }

    const { digitalData } = window;
    if (!digitalData) return;

    const eventType = digitalData.event?.eventInfo?.eventType;

    // fire account_start event the first time user clicks in a field on the screen
    if (value === '' && eventType !== 'Account Form Start') {
      dispatch(updateProductAnalytics('account_start', !!offerFlow));
    }
  };

  // maxlength is not an attribute of an input field of type number:
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/number
  // https://stackoverflow.com/questions/18510845/maxlength-ignored-for-input-type-number-in-chrome
  const handleInput = (event: FormEvent<HTMLInputElement>) => {
    if (type !== 'number' || !maxLength) return;
    if (event.currentTarget.value.length > maxLength) {
      event.currentTarget.value = event.currentTarget.value.slice(0, maxLength);
    }
  };

  // TODO(Investigate): We used to persist the entire InputEvent to the signupFormChanged function,
  // but a recent change seems to have introduced a race condition (repro'd by rapid typing :-)) that leads to
  // us trying to access the event's properties after it's been nulled out.
  // See https://reactjs.org/docs/events.html#event-pooling for a better description.
  const onFormChanged = (event: InputEvent) => {
    let { value: changedValue } = event.target;
    if (fieldName === 'phone') {
      changedValue = changedValue.replace(/[^0-9]/gi, '');
    }
    dispatch(signupFormChanged({ field: fieldName, value: changedValue }));
    if (fieldName === 'country') dispatch(getRegions(changedValue));
  };

  return (
    <>
      <FormInput
        disabled={disabled}
        type={type || 'text'}
        value={value}
        name={fieldName}
        id={fieldName}
        onInput={handleInput}
        maxLength={maxLength}
        isInvalid={fieldMissingOrInvalid}
        readOnly={readOnly}
        onBlur={() => dispatch(clearFieldForValidation(fieldName))}
        onFocus={handleFocus}
        onChange={onFormChanged}
        placeholder={placeholder}
        width={width}
        marginAboveError={marginAboveError ?? ''}
      />

      {fieldMissingOrInvalid && (
        <FieldErrorMessage className={`field-error-message${addPadding ? ' margin-all-none' : ''}`}>
          <p>{getFieldErrorMessage(fieldName, value)}</p>
        </FieldErrorMessage>
      )}
    </>
  );
};

export default FormInputComponent;
